import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiCheck, FiChevronRight, FiDownload, FiEdit2, FiMinus, FiMousePointer, FiPlus, FiRotateCcw, FiRotateCw, FiSave, FiTrash2, FiType, FiX } from "react-icons/fi";
import { DOCUMENT_UNITS, eraseAlongSegment, loadAnnotations, recognizeInkShape, pointInBounds, saveAnnotations, selectionBounds, selectWithLasso, strokeOutline, translateAnnotations } from "./recordHandwritingGeometry";
import { exportHandwritingSelection, exportRecordHandwritingPdf } from "./recordHandwritingPdf";

import { usePointerDragAutoScroll } from "../../shared/dragAutoScroll";

const colors = ["#252d3d", "#2463eb", "#f59e0b", "#ffffff", "#e44b53", "#929292", "#996b35", "#2b965b", "#f1c40f"];
const DOCUMENT_WIDTH = 960;
const tools = [
  { id: "lasso", label: "套索選取", icon: <OutlineIcon kind="lasso" /> },
  { id: "pen", label: "毛筆", icon: <OutlineIcon kind="pen" /> },
  { id: "pencil", label: "鉛筆", icon: <FiEdit2 /> },
  { id: "highlighter", label: "螢光筆", icon: <OutlineIcon kind="highlighter" /> },
  { id: "eraser", label: "橡皮擦", icon: <OutlineIcon kind="eraser" /> },
  { id: "text", label: "文字", icon: <FiType /> },
  { id: "hand", label: "拖曳瀏覽", icon: <OutlineIcon kind="hand" /> },
];

function OutlineIcon({ kind }) {
  const paths = {
    lasso: <><path d="M8 20c-5-1-5-5-3-10S13 2 17 4s3 9-1 13-9 2-8-1 5-2 5 2-1 5-4 5" strokeDasharray="3 2" /></>,
    pen: <><path d="m9 13 9-10a2 2 0 0 1 3 3L11 15M9 13l2 2M9 13c-4-1-6 2-6 5 0 2-1 3-1 3 5 1 10-2 9-6M3 18c2 0 3-1 4-3" /></>,
    highlighter: <><path d="m5 17 8-13 8 5-8 13-8-5Zm3 2-2 3H2l4-5M12 6l8 5" /></>,
    eraser: <><path d="m3 14 10-11a2 2 0 0 1 3 0l6 6a2 2 0 0 1 0 3L13 22H9l-6-5a2 2 0 0 1 0-3Zm4-4 10 9M13 22h9" /></>,
    hand: <><path d="M8 13V6a1.5 1.5 0 0 1 3 0v5-8a1.5 1.5 0 0 1 3 0v8-6a1.5 1.5 0 0 1 3 0v7-4a1.5 1.5 0 0 1 3 0v7c0 5-3 7-7 7-3 0-5-2-7-5l-3-4c-1-2 1-4 3-2l2 2Z" /></>,
  };
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[kind]}</svg>;
}

const makeDocument = (key, persistent = true) => {
  const stored = persistent ? loadAnnotations(key) : { entries: [], error: "" };
  return { key, persistent, ...stored, savedEntries: stored.needsSave ? null : stored.entries, readError: Boolean(stored.readError), past: [], future: [] };
};
let nextId = 0;
const annotationId = () => `ink-${Date.now().toString(36)}-${(++nextId).toString(36)}`;

function scrollContainers(element) {
  const result = [];
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const style = window.getComputedStyle(parent);
    const canScrollX = /(auto|scroll)/.test(style.overflowX || style.overflow);
    const canScrollY = /(auto|scroll)/.test(style.overflowY || style.overflow);
    if (canScrollX || canScrollY) result.push({ element: parent, left: parent.scrollLeft, top: parent.scrollTop, canScrollX, canScrollY });
  }
  return result;
}

export default function RecordHandwriting({ storageKey, active, documentTitle = "講義與考卷", persistent = true, responsive = false, allowPdfExport = true, children }) {
  const autoScroll = usePointerDragAutoScroll();
  const [documentState, setDocumentState] = useState(() => makeDocument(storageKey, persistent));
  const documentRef = useRef(documentState);
  const activeRef = useRef(active);
  const currentKeyRef = useRef(storageKey);
  const surfaceRef = useRef(null);
  const previewRef = useRef(null);
  const viewportRef = useRef(null);
  const toolbarRef = useRef(null);
  const eraserHoldRef = useRef({ timer: null, fired: false });
  useEffect(() => {
    const hold = eraserHoldRef.current;
    return () => { clearTimeout(hold.timer); hold.fired = false; };
  }, [storageKey, active]);
  const blockedToolbarPointerRef = useRef(false);
  const panelTriggerRef = useRef(null);
  const toolPopoverRef = useRef(null);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const gestureRef = useRef(null);
  const shapeTimerRef = useRef(null);
  const draftFrameRef = useRef(null);
  const [smartShapes, setSmartShapes] = useState(true);
  const [tool, setTool] = useState("hand");
  const previousToolRef = useRef("hand");
  const previousPenRef = useRef("pen");
  const [color, setColor] = useState(colors[1]);
  const [widths, setWidths] = useState({ pen: 24, pencil: 24, highlighter: 24, eraser: 12 });
  const width = widths[tool] || widths.pen;
  const setWidth = (value) => setWidths((previous) => ({ ...previous, [tool]: value }));
  const [textSize, setTextSize] = useState(24);
  const [toolPanel, setToolPanel] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [actionNotice, setActionNotice] = useState("");
  const [fingerDrawing, setFingerDrawing] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [dimensions, setDimensions] = useState({ width: DOCUMENT_WIDTH, height: 1, viewport: DOCUMENT_WIDTH });
  const [draft, setDraft] = useState(null);
  const [previewEntries, setPreviewEntries] = useState(null);
  const [eraserPreview, setEraserPreview] = useState(null);
  const [lasso, setLasso] = useState([]);
  const [selection, setSelection] = useState([]);
  const [selectionRegion, setSelectionRegion] = useState(null);
  const [inkMenu, setInkMenu] = useState(null);
  const inkClipboardRef = useRef(null);
  const pressTimerRef = useRef(null);
  const pressOriginRef = useRef(null);
  const [textEditor, setTextEditor] = useState(null);
  const textRef = useRef(null);
  const textEditorRef = useRef(null);
  const mountedRef = useRef(true);
  const exportTaskRef = useRef(null);
  const [exportState, setExportState] = useState({ key: storageKey, status: "idle", message: "" });
  activeRef.current = active;
  currentKeyRef.current = storageKey;
  textEditorRef.current = textEditor;

  const commit = (entries) => {
    const previous = documentRef.current;
    if (previous.key !== currentKeyRef.current) return;
    const next = { ...previous, entries, past: [...previous.past, previous.entries].slice(-70), future: [], error: previous.readError ? previous.error : "" };
    documentRef.current = next;
    setDocumentState(next);
  };

  const saveText = () => {
    const editor = textEditorRef.current;
    if (!editor || editor.key !== currentKeyRef.current) return;
    const text = editor.text.trim();
    if (text) {
      const entry = { id: editor.id || annotationId(), type: "text", x: editor.x, y: editor.y, text, fontSize: editor.fontSize, color: editor.color, width: 1 };
      const entries = documentRef.current.entries;
      commit(editor.id ? entries.map((item) => item.id === editor.id ? entry : item) : [...entries, entry]);
    }
    textEditorRef.current = null;
    setTextEditor(null);
  };

  const cancelLongPress = () => { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; pressOriginRef.current = null; };

  const openInkMenu = (kind, point) => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    const scale = (rect?.width || DOCUMENT_WIDTH) / DOCUMENT_UNITS;
    setInkMenu({ kind, point, left: Math.max(8, Math.min(window.innerWidth - 208, (rect?.left || 0) + point.x * scale)), top: Math.max(8, Math.min(window.innerHeight - (kind === "selection" ? 230 : 60), (rect?.top || 0) + point.y * scale)) });
  };

  const resetGesture = () => {
    if (draftFrameRef.current !== null) cancelAnimationFrame(draftFrameRef.current);
    draftFrameRef.current = null;
    autoScroll.stop();
    cancelLongPress();
    clearTimeout(shapeTimerRef.current);
    gestureRef.current = null;
    setDraft(null);
    setLasso([]);
    setPreviewEntries(null);
    setEraserPreview(null);
  };

  const scheduleDraft = (gesture) => {
    if (draftFrameRef.current !== null) return;
    draftFrameRef.current = requestAnimationFrame(() => {
      draftFrameRef.current = null;
      if (mountedRef.current && gestureRef.current === gesture) {
        setDraft({ ...gesture.stroke, points: [...gesture.stroke.points] });
      }
    });
  };

  const finishGesture = () => {
    const gesture = gestureRef.current;
    if (gesture && gesture.key === currentKeyRef.current) {
      if (gesture.kind === "draw") {
        // Commit the full input buffer, including samples not painted by the next frame yet.
        const stroke = { ...gesture.stroke, points: [...gesture.stroke.points] };
        commit([...documentRef.current.entries, stroke]);
      }
      if (gesture.kind === "erase" && gesture.entries.length !== documentRef.current.entries.length) commit(gesture.entries);
      if (gesture.kind === "move" && (gesture.dx || gesture.dy)) commit(gesture.entries);
      if (gesture.kind === "lasso") {
        const ids = selectWithLasso(documentRef.current.entries, gesture.points);
        setSelection(ids);
        const points = gesture.points;
        const left = Math.min(...points.map((p) => p.x)); const top = Math.min(...points.map((p) => p.y));
        const bounds = points.length >= 3 ? { x: left, y: top, width: Math.max(...points.map((p) => p.x)) - left, height: Math.max(...points.map((p) => p.y)) - top } : null;
        setSelectionRegion(bounds && bounds.width > 5 && bounds.height > 5 ? bounds : null);
        if (bounds && bounds.width > 5 && bounds.height > 5) openInkMenu("selection", { x: bounds.x + bounds.width, y: bounds.y });
      }
      if (gesture.kind === "move") {
        setSelectionRegion(null);
        const bounds = selectionBounds(gesture.entries, gesture.ids);
        if (bounds) openInkMenu("selection", { x: bounds.x + bounds.width, y: bounds.y });
      }
    }
    resetGesture();
  };

  const saveHandwriting = () => {
    if (!persistent) return;
    finishGesture();
    saveText();
    const previous = documentRef.current;
    if (previous.key !== currentKeyRef.current || previous.readError) return;
    const error = saveAnnotations(previous.key, previous.entries);
    const next = { ...previous, error, savedEntries: error ? previous.savedEntries : previous.entries };
    documentRef.current = next;
    setDocumentState(next);
    setActionNotice(error ? "" : "已儲存這份手寫");
  };

  const exportPdf = async () => {
    if (exportTaskRef.current || !previewRef.current) return;
    const task = { key: storageKey };
    exportTaskRef.current = task;
    finishGesture();
    saveText();
    // Keep the export independent from later ink edits or another opened record.
    const annotations = documentRef.current.entries.map((entry) => ({
      ...entry,
      ...(entry.points ? { points: entry.points.map((point) => ({ ...point })) } : {}),
    }));
    setExportState({ key: task.key, status: "busy", message: "正在匯出含手寫的 PDF…" });
    const isCurrent = () => mountedRef.current && currentKeyRef.current === task.key && exportTaskRef.current === task;
    try {
      await exportRecordHandwritingPdf({
        documentElement: previewRef.current,
        annotations,
        title: documentTitle,
        filename: `${documentTitle}_含手寫.pdf`,
      });
      if (isCurrent()) setExportState({ key: task.key, status: "success", message: "已匯出含手寫的 PDF" });
    } catch (error) {
      if (isCurrent()) setExportState({ key: task.key, status: "error", message: `PDF 匯出失敗：${error?.message || "請稍後再試"}` });
    } finally {
      if (isCurrent()) exportTaskRef.current = null;
    }
  };

  useLayoutEffect(() => {
    if (documentRef.current.key === storageKey && documentRef.current.persistent === persistent) return;
    // A record switch discards edits that the user has not explicitly saved.
    const next = makeDocument(storageKey, persistent);
    documentRef.current = next;
    setDocumentState(next);
    resetGesture();
    setSelection([]); setSelectionRegion(null);
    setInkMenu(null);
    inkClipboardRef.current = null;
    setToolPanel(null);
    setActionNotice("");
    setTextEditor(null);
    textEditorRef.current = null;
    exportTaskRef.current = null;
    setExportState({ key: storageKey, status: "idle", message: "" });
    setZoom(1);
    // Every other value comes from refs so a key change cannot capture the new record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, persistent]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      exportTaskRef.current = null;
      clearTimeout(pressTimerRef.current);
      clearTimeout(shapeTimerRef.current);
      if (draftFrameRef.current !== null) cancelAnimationFrame(draftFrameRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!active) {
      finishGesture();
      saveText();
      setSelection([]); setSelectionRegion(null);
      setToolPanel(null);
      setInkMenu(null);
    }
    if (active) { setTool("hand"); setSelection([]); setSelectionRegion(null); setInkMenu(null); }
    // Tool gestures are finished once when handwriting mode closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useLayoutEffect(() => {
    const measure = () => {
      const viewport = viewportRef.current?.clientWidth || viewportRef.current?.getBoundingClientRect().width;
      const height = previewRef.current?.offsetHeight || previewRef.current?.getBoundingClientRect().height;
      if (viewport > 0 && height > 0) setDimensions((previous) => previous.viewport === viewport && previous.height === height ? previous : { width: responsive ? viewport : DOCUMENT_WIDTH, height, viewport });
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (previewRef.current) observer?.observe(previewRef.current);
    if (viewportRef.current) observer?.observe(viewportRef.current);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [storageKey, responsive]);

  useLayoutEffect(() => {
    if (!active || tool === "hand" || !viewportRef.current) return undefined;
    // Lock native scrolling too: palm contact and wheel events must not move the paper.
    const containers = scrollContainers(viewportRef.current).map(({ element }) => ({
      element, overflow: element.style.overflow, overscroll: element.style.overscrollBehavior,
    }));
    containers.forEach(({ element }) => {
      element.style.overflow = "hidden";
      element.style.overscrollBehavior = "none";
    });
    const viewport = viewportRef.current;
    const preventScroll = (event) => {
      if (!event.target.closest?.("textarea, input")) event.preventDefault();
    };
    viewport.addEventListener("wheel", preventScroll, { passive: false });
    viewport.addEventListener("touchmove", preventScroll, { passive: false });
    return () => {
      containers.forEach(({ element, overflow, overscroll }) => {
        element.style.overflow = overflow;
        element.style.overscrollBehavior = overscroll;
      });
      viewport.removeEventListener("wheel", preventScroll);
      viewport.removeEventListener("touchmove", preventScroll);
    };
  }, [active, tool]);

  useEffect(() => { if (textEditor) textRef.current?.focus(); }, [textEditor]);

  useLayoutEffect(() => {
    if (!toolPanel) { setPopoverPosition(null); return undefined; }
    const panel = toolPopoverRef.current;
    const trigger = panelTriggerRef.current;
    const wrap = toolbarRef.current;
    if (!panel || !trigger || !wrap) return undefined;
    const visualViewport = window.visualViewport;
    const reposition = () => {
      const anchor = trigger.getBoundingClientRect();
      const origin = wrap.getBoundingClientRect();
      const panelBox = panel.getBoundingClientRect();
      const toolbarBox = trigger.closest('[role="toolbar"]')?.getBoundingClientRect();
      if (toolbarBox?.height > 0 && (anchor.bottom <= toolbarBox.top || anchor.top >= toolbarBox.bottom)) {
        setToolPanel(null);
        return;
      }
      const visible = {
        left: visualViewport?.offsetLeft || 0, top: visualViewport?.offsetTop || 0,
        right: (visualViewport?.offsetLeft || 0) + (visualViewport?.width || window.innerWidth),
        bottom: (visualViewport?.offsetTop || 0) + (visualViewport?.height || window.innerHeight),
      };
      // Stay inside the record modal's scroll area as well as the visible screen.
      for (let parent = wrap.parentElement; parent; parent = parent.parentElement) {
        const style = window.getComputedStyle(parent);
        const clipX = /(auto|scroll|hidden|clip)/.test(style.overflowX || style.overflow);
        const clipY = /(auto|scroll|hidden|clip)/.test(style.overflowY || style.overflow);
        if (!clipX && !clipY) continue;
        const bounds = parent.getBoundingClientRect();
        if (clipX && bounds.width > 0) { visible.left = Math.max(visible.left, bounds.left); visible.right = Math.min(visible.right, bounds.right); }
        if (clipY && bounds.height > 0) { visible.top = Math.max(visible.top, bounds.top); visible.bottom = Math.min(visible.bottom, bounds.bottom); }
      }
      const maxWidth = Math.max(1, visible.right - visible.left - 16);
      const maxHeight = Math.max(1, visible.bottom - visible.top - 16);
      const width = Math.min(panelBox.width || 220, maxWidth);
      const height = Math.min(panelBox.height || panel.scrollHeight, maxHeight);
      let left = anchor.right + 8;
      if (left + width > visible.right - 8) left = anchor.left - width - 8;
      left = Math.max(visible.left + 8, Math.min(left, visible.right - width - 8));
      const top = Math.max(visible.top + 8, Math.min(anchor.top, visible.bottom - height - 8));
      const next = { left: left - origin.left, top: top - origin.top, maxWidth, maxHeight };
      setPopoverPosition((previous) => previous && Object.keys(next).every((key) => previous[key] === next[key]) ? previous : next);
    };
    reposition();
    document.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    visualViewport?.addEventListener("resize", reposition);
    visualViewport?.addEventListener("scroll", reposition);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(reposition);
    observer?.observe(panel);
    observer?.observe(trigger);
    observer?.observe(wrap);
    return () => {
      document.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      visualViewport?.removeEventListener("resize", reposition);
      visualViewport?.removeEventListener("scroll", reposition);
      observer?.disconnect();
    };
  }, [toolPanel]);

  useEffect(() => {
    if (!toolPanel) return undefined;
    const dismiss = (event) => { if (!toolbarRef.current?.contains(event.target)) setToolPanel(null); };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [toolPanel]);

  useEffect(() => {
    if (!inkMenu) return undefined;
    const dismiss = (event) => { if (!event.target.closest?.("[data-ink-menu]")) setInkMenu(null); };
    const dismissOnLayout = () => setInkMenu(null);
    document.addEventListener("pointerdown", dismiss);
    window.addEventListener("resize", dismissOnLayout);
    return () => { document.removeEventListener("pointerdown", dismiss); window.removeEventListener("resize", dismissOnLayout); };
  }, [inkMenu]);

  const restoreHistory = (direction) => {
    setInkMenu(null);
    const previous = documentRef.current;
    if (gestureRef.current) resetGesture();
    if (textEditorRef.current) saveText();
    const current = documentRef.current;
    const stack = direction === "undo" ? current.past : current.future;
    if (!stack.length || previous.key !== storageKey) return;
    const entries = stack[stack.length - 1];
    const next = { ...current, entries, past: direction === "undo" ? current.past.slice(0, -1) : [...current.past, current.entries],
      future: direction === "undo" ? [...current.future, current.entries] : current.future.slice(0, -1), error: current.readError ? current.error : "" };
    documentRef.current = next;
    setDocumentState(next);
    setSelection([]); setSelectionRegion(null);
    setActionNotice("");
  };

  const clearHandwriting = () => {
    finishGesture();
    setInkMenu(null);
    saveText();
    if (!documentRef.current.entries.length) return;
    commit([]);
    setSelection([]); setSelectionRegion(null);
    setActionNotice("");
  };

  const cancelEraserHold = () => {
    clearTimeout(eraserHoldRef.current.timer);
    eraserHoldRef.current.timer = null;
  };
  const startEraserHold = (event) => {
    if (event.button !== 0 || gestureRef.current || blockedToolbarPointerRef.current || exporting) return;
    event.preventDefault();
    cancelEraserHold();
    eraserHoldRef.current.fired = false;
    eraserHoldRef.current.timer = setTimeout(() => {
      eraserHoldRef.current.fired = true;
      clearHandwriting();
    }, 800);
  };
  const clickEraser = () => {
    cancelEraserHold();
    if (eraserHoldRef.current.fired) { eraserHoldRef.current.fired = false; return; }
    toggleEraser();
  };

  const pointFromEvent = (event, pointerType = event.pointerType, box = surfaceRef.current?.getBoundingClientRect()) => {
    const scale = DOCUMENT_UNITS / (box?.width || dimensions.width);
    const penPressure = pointerType === "pen" && Number.isFinite(event.pressure);
    // Preserve the original pressure response of each drawing tool.
    const pressure = penPressure && tool === "pen" ? Math.max(0, Math.min(1, event.pressure))
      : penPressure && event.pressure > 0 ? Math.max(0.1, Math.min(1, event.pressure)) : 0.65;
    return { x: (event.clientX - (box?.left || 0)) * scale, y: (event.clientY - (box?.top || 0)) * scale,
      p: pressure };
  };

  const armShapeRecognition = (gesture) => {
    if (!smartShapes || gesture.kind !== "draw") return;
    const endpoint = gesture.stroke.points[gesture.stroke.points.length - 1];
    // Measure hold tolerance in screen pixels so tablet zoom does not amplify tremor.
    const tolerance = 8 * gesture.unitsPerPixel;
    if (gesture.holdAnchor && Math.hypot(endpoint.x - gesture.holdAnchor.x, endpoint.y - gesture.holdAnchor.y) < tolerance) return;
    gesture.holdAnchor = endpoint;
    gesture.holdPointCount = gesture.stroke.points.length;
    gesture.holdTolerance = tolerance;
    clearTimeout(shapeTimerRef.current);
    shapeTimerRef.current = setTimeout(() => {
      if (gestureRef.current !== gesture || !activeRef.current) return;
      const shape = recognizeInkShape(gesture.stroke.points.slice(0, gesture.holdPointCount));
      if (!shape) return;
      gesture.rawStroke = gesture.stroke;
      gesture.stroke = { ...gesture.stroke, points: shape.points };
      gesture.snapped = true;
      setDraft(gesture.stroke);
      setActionNotice(`已修正為${shape.label}；移動筆尖可繼續自由書寫`);
    }, 700);
  };

  const onPointerDown = (event) => {
    if (!activeRef.current || exportTaskRef.current || gestureRef.current || (event.button !== undefined && event.button !== 0)) return;
    setActionNotice("");
    setInkMenu(null);
    event.preventDefault();
    const mayPaste = inkClipboardRef.current && (event.pointerType === "touch" || tool === "lasso");
    if (mayPaste) {
      const point = pointFromEvent(event);
      pressOriginRef.current = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
      pressTimerRef.current = setTimeout(() => {
        resetGesture();
        setSelection([]); setSelectionRegion(null);
        openInkMenu("paste", point);
      }, 550);
    }
    if (event.pointerType === "touch" && !fingerDrawing && tool !== "hand" && tool !== "lasso") return;
    event.currentTarget.focus({ preventScroll: true });
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (error) { /* Synthetic pointer events have no native capture. */ }
    const bounds = surfaceRef.current?.getBoundingClientRect();
    const point = pointFromEvent(event, event.pointerType, bounds);
    const common = { key: storageKey, pointerId: event.pointerId, unitsPerPixel: DOCUMENT_UNITS / (bounds?.width || dimensions.width) };
    if (tool === "hand") {
      gestureRef.current = { ...common, kind: "pan", x: event.clientX, y: event.clientY, containers: scrollContainers(surfaceRef.current) };
      return;
    }
    if (textEditorRef.current) saveText();
    if (tool === "text") {
      const textId = event.target.closest?.("[data-text-annotation]")?.getAttribute("data-text-annotation");
      const existing = documentRef.current.entries.find((item) => item.id === textId);
      if (existing) { setColor(existing.color); setTextSize(existing.fontSize); }
      setTextEditor({ key: storageKey, ...(existing || { x: point.x, y: point.y, text: "", color, fontSize: textSize }) });
      setSelection([]); setSelectionRegion(null);
      return;
    }
    if (tool === "lasso") {
      autoScroll.start(event);
      const bounds = selectionBounds(documentRef.current.entries, selection);
      if (pointInBounds(point, bounds)) {
        gestureRef.current = { ...common, kind: "move", start: point, ids: selection, original: documentRef.current.entries, entries: documentRef.current.entries, dx: 0, dy: 0 };
      } else {
        setSelection([]); setSelectionRegion(null);
        gestureRef.current = { ...common, kind: "lasso", points: [point] };
        setLasso([point]);
      }
      return;
    }
    setSelection([]); setSelectionRegion(null);
    if (tool === "eraser") {
      const radius = width / 2;
      const entries = eraseAlongSegment(documentRef.current.entries, point, point, radius);
      gestureRef.current = { ...common, kind: "erase", point, points: [point], radius, entries };
      setPreviewEntries(entries);
      setEraserPreview({ points: [point], radius });
      return;
    }
    // Each physical pen contact is its own stroke; nearby letters must never join on lift.
    const stroke = { id: annotationId(), type: "stroke", tool, ...(tool === "pen" ? { brush: true } : {}), color, width: tool === "highlighter" ? width * 6 : width, points: [point] };
    gestureRef.current = { ...common, kind: "draw", stroke };
    setDraft({ ...stroke, points: [...stroke.points] });
    armShapeRecognition(gestureRef.current);
  };

  const onPointerMove = (event) => {
    const origin = pressOriginRef.current;
    if (origin && origin.pointerId === event.pointerId && Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > 6) cancelLongPress();
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId || gesture.key !== storageKey) return;
    event.preventDefault();
    if (gesture.kind === "pan") {
      const dx = gesture.x - event.clientX; const dy = gesture.y - event.clientY;
      let horizontalDone = false; let verticalDone = false;
      gesture.containers.forEach(({ element, left, top, canScrollX, canScrollY }) => {
        if (!horizontalDone && canScrollX && element.scrollWidth > element.clientWidth) { element.scrollLeft = left + dx; horizontalDone = true; }
        if (!verticalDone && canScrollY && element.scrollHeight > element.clientHeight) { element.scrollTop = top + dy; verticalDone = true; }
      });
      return;
    }
    // A coalesced Pencil event can contain many samples; measure layout only once.
    const bounds = surfaceRef.current?.getBoundingClientRect();
    gesture.unitsPerPixel = DOCUMENT_UNITS / (bounds?.width || dimensions.width);
    const events = typeof event.nativeEvent?.getCoalescedEvents === "function" ? event.nativeEvent.getCoalescedEvents() : [];
    const points = (events.length ? [...events, event] : [event]).map((sample) => pointFromEvent(sample, sample.pointerType || event.pointerType, bounds));
    if (gesture.snapped) {
      const point = points[points.length - 1];
      if (Math.hypot(point.x - gesture.holdAnchor.x, point.y - gesture.holdAnchor.y) < gesture.holdTolerance * 1.5) return;
      gesture.stroke = gesture.rawStroke;
      gesture.snapped = false;
      setActionNotice("");
    }
    const drawingPoints = gesture.kind === "draw" ? gesture.stroke.points : null;
    let drawingChanged = false;
    const erasingPoints = gesture.kind === "erase" ? [...gesture.points] : null;
    points.forEach((point, index) => {
      if (gesture.kind === "draw") {
        const finalSample = event.type === "pointerup" && index === points.length - 1;
        const last = drawingPoints[drawingPoints.length - 1];
        const distance = Math.hypot(point.x - last.x, point.y - last.y);
        const moved = distance >= 0.3 || (finalSample && distance > 0);
        const release = gesture.stroke.brush && finalSample && point.p === 0 && last.p !== 0;
        const heavierContact = gesture.stroke.brush && point.p > last.p;
        if (moved || release || heavierContact) {
          // Buffer every sample immediately, then paint once per display frame.
          if (moved || release) drawingPoints.push(moved ? point : { ...last, p: 0 });
          else drawingPoints[drawingPoints.length - 1] = { ...last, p: point.p };
          drawingChanged = true;
        }
      }
      if (gesture.kind === "erase") {
        gesture.entries = eraseAlongSegment(gesture.entries, gesture.point, point, gesture.radius);
        if (Math.hypot(point.x - gesture.point.x, point.y - gesture.point.y) > 0) erasingPoints.push(point);
        gesture.point = point;
      }
      if (gesture.kind === "lasso") gesture.points = [...gesture.points, point];
      if (gesture.kind === "move") {
        gesture.dx = point.x - gesture.start.x; gesture.dy = point.y - gesture.start.y;
        gesture.entries = translateAnnotations(gesture.original, gesture.ids, gesture.dx, gesture.dy);
      }
    });
    if (gesture.kind === "draw" && drawingChanged) {
      scheduleDraft(gesture);
      if (event.type !== "pointerup") armShapeRecognition(gesture);
    }
    if (gesture.kind === "erase" || gesture.kind === "move") setPreviewEntries(gesture.entries);
    if (gesture.kind === "erase") {
      gesture.points = erasingPoints;
      setEraserPreview({ points: erasingPoints, radius: gesture.radius });
    }
    if (gesture.kind === "lasso") setLasso(gesture.points);
  };

  const onPointerUp = (event) => {
    cancelLongPress();
    if (gestureRef.current?.pointerId !== event.pointerId) return;
    const wasErasing = gestureRef.current.kind === "erase";
    onPointerMove(event);
    finishGesture();
    if (wasErasing) changeTool(previousToolRef.current);
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch (error) { /* Capture may already be released by the browser. */ }
  };

  const captureSelection = async () => {
    const bounds = selectionRegion || selectionBounds(documentRef.current.entries, selection);
    if (!bounds || exportTaskRef.current) return;
    const task = { key: storageKey };
    exportTaskRef.current = task;
    setInkMenu(null);
    setExportState({ key: task.key, status: "busy", message: "正在產生區塊截圖…" });
    try {
      await exportHandwritingSelection({ documentElement: previewRef.current, annotations: documentRef.current.entries, bounds, title: documentTitle });
      if (mountedRef.current && exportTaskRef.current === task) setExportState({ key: task.key, status: "success", message: "已下載區塊截圖" });
    } catch (error) {
      if (mountedRef.current && exportTaskRef.current === task) setExportState({ key: task.key, status: "error", message: `區塊截圖失敗：${error.message || "請稍後再試"}` });
    } finally { if (exportTaskRef.current === task) exportTaskRef.current = null; }
  };

  const changeTool = (nextTool) => { if (["pen", "pencil", "highlighter"].includes(nextTool)) previousPenRef.current = nextTool; finishGesture(); saveText(); if (nextTool === "eraser" && tool !== "eraser") previousToolRef.current = tool; setInkMenu(null); setTool(nextTool); setToolPanel(null); if (nextTool !== "lasso") { setSelection([]); setSelectionRegion(null); } };
  const toggleHand = () => changeTool(tool === "hand" ? previousPenRef.current : "hand");
  const toggleEraser = () => changeTool(tool === "eraser" ? previousToolRef.current : "eraser");
  const onToolbarPointerDown = (event) => {
    blockedToolbarPointerRef.current = Boolean(gestureRef.current);
    if (blockedToolbarPointerRef.current) { event.preventDefault(); event.stopPropagation(); }
  };
  const guardToolbarActivation = (event) => {
    if (event.detail === 0 && !event.nativeEvent?.pointerType) return;
    // A palm's click can arrive after the writing pointer has already lifted.
    if (gestureRef.current || blockedToolbarPointerRef.current) {
      event.preventDefault(); event.stopPropagation();
    }
  };
  const changeColor = (value) => { setColor(value); setTextEditor((editor) => editor ? { ...editor, color: value } : editor); };
  const changeTextSize = (value) => { setTextSize(value); setTextEditor((editor) => editor ? { ...editor, fontSize: value } : editor); };
  const deleteSelection = () => { if (selection.length) { commit(documentRef.current.entries.filter((item) => !selection.includes(item.id))); setSelection([]); setSelectionRegion(null); setInkMenu(null); } };
  const cloneInk = (entries) => entries.map((entry) => ({ ...entry, ...(entry.points ? { points: entry.points.map((point) => ({ ...point })) } : {}) }));
  const copySelection = (cut = false) => {
    const selected = documentRef.current.entries.filter((entry) => selection.includes(entry.id));
    if (!selected.length) return;
    inkClipboardRef.current = cloneInk(selected);
    if (cut) deleteSelection();
    setInkMenu(null);
  };
  const insertInk = (entries, dx, dy) => {
    const copies = cloneInk(entries).map((entry) => ({ ...entry, id: annotationId() }));
    const ids = copies.map((entry) => entry.id);
    commit([...documentRef.current.entries, ...translateAnnotations(copies, ids, dx, dy)]);
    setSelection(ids);
    setSelectionRegion(null);
    setInkMenu(null);
  };
  const duplicateSelection = () => insertInk(documentRef.current.entries.filter((entry) => selection.includes(entry.id)), 24, 24);
  const pasteInk = () => {
    const clipboard = inkClipboardRef.current;
    if (!clipboard || !inkMenu) return;
    const bounds = selectionBounds(clipboard, clipboard.map((entry) => entry.id));
    if (bounds) insertInk(clipboard, inkMenu.point.x - bounds.x, inkMenu.point.y - bounds.y);
  };
  const onKeyDown = (event) => {
    if (event.key === "Escape" && toolPanel) { event.preventDefault(); event.stopPropagation(); panelTriggerRef.current?.focus({ preventScroll: true }); setToolPanel(null); return; }
    if (!active || exportTaskRef.current || /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) return;
    if ((event.metaKey || event.ctrlKey) && ["z", "y"].includes(event.key.toLowerCase())) {
      event.preventDefault(); event.stopPropagation(); restoreHistory(event.key.toLowerCase() === "y" || event.shiftKey ? "redo" : "undo");
    }
    if ((event.key === "Delete" || event.key === "Backspace") && selection.length) { event.preventDefault(); deleteSelection(); }
    if (event.key === "Escape") { event.stopPropagation(); setInkMenu(null); resetGesture(); setSelection([]); setSelectionRegion(null); if (toolPanel) panelTriggerRef.current?.focus(); setToolPanel(null); }
  };

  const entries = documentState.key === storageKey ? (previewEntries || documentState.entries) : [];
  const changedEntries = useMemo(() => documentState.savedEntries === null || JSON.stringify(documentState.entries) !== JSON.stringify(documentState.savedEntries), [documentState.entries, documentState.savedEntries]);
  const unsaved = changedEntries || Boolean(draft || previewEntries || textEditor?.text.trim());
  const box = selectionRegion || selectionBounds(entries, selection);
  const effectiveZoom = Math.min(1, dimensions.viewport / dimensions.width) * zoom;
  const docHeight = dimensions.height / dimensions.width * DOCUMENT_UNITS;
  const isWidthControl = ["pen", "pencil", "highlighter", "eraser", "text"].includes(tool);
  const isTextTool = tool === "text";
  const isEraserTool = tool === "eraser";
  const sizeLabel = isTextTool ? "文字大小" : isEraserTool ? "橡皮擦範圍" : "筆畫粗細";
  const selectedSize = isTextTool ? textSize : width;
  const sizePresets = isTextTool ? [12, 18, 24, 36, 48] : isEraserTool ? [1, 12, 24, 48, 72] : [20, 24, 26, 28, 32];
  const toggleColorPanel = (event) => { setShowOptions(false); panelTriggerRef.current = event.currentTarget; setToolPanel((previous) => previous === "color" ? null : "color"); };
  const toggleWidthPanel = (event) => { setShowOptions(false); panelTriggerRef.current = event.currentTarget; setToolPanel((previous) => previous === "width" ? null : "width"); };
  const exporting = exportState.key === storageKey && exportState.status === "busy";
  const exportFeedback = exportState.key === storageKey ? exportState.message : "";
  const exportButton = <PdfExportButton type="button" aria-label="匯出含手寫的 PDF" title="匯出含手寫的 PDF" disabled={exporting} onClick={exportPdf}><FiDownload /><span>{exporting ? "匯出中" : "PDF"}</span></PdfExportButton>;

  const handToggleTool = tools.find((item) => item.id === (tool === "hand" ? "hand" : previousPenRef.current));

  return <Wrapper $active={active} $hasSidebar={active || allowPdfExport} onKeyDown={onKeyDown} aria-busy={exporting}>
    {active && <ToolbarWrap ref={toolbarRef} onPointerDownCapture={onToolbarPointerDown} onClickCapture={guardToolbarActivation} onDoubleClickCapture={guardToolbarActivation}>
      <HandButton type="button" data-handwriting-hand-toggle aria-label="手掌／筆切換" title="切換手掌／上一支筆" aria-pressed={tool === "hand"} $selected={tool === "hand"} disabled={exporting} onClick={toggleHand}>{handToggleTool.icon}<span>{handToggleTool.label}</span></HandButton>
      <EraserButton type="button" aria-label="橡皮擦" title="短按切換橡皮擦；長按清除整份手寫，可按復原還原" aria-pressed={tool === "eraser"} $selected={tool === "eraser"} disabled={exporting} onPointerDown={startEraserHold} onPointerUp={cancelEraserHold} onPointerLeave={cancelEraserHold} onPointerCancel={cancelEraserHold} onBlur={cancelEraserHold} onContextMenu={(event) => event.preventDefault()} onClick={clickEraser}><OutlineIcon kind="eraser" /><small>橡皮擦</small></EraserButton>
      <Toolbar role="toolbar" aria-label="手寫工具列">
        <PrimaryRow><PrimaryTools>{tools.filter((item) => item.id !== "eraser").map((item) => <React.Fragment key={item.id}>
          {item.id === "hand" && <ToolButton type="button" title="新增註記" aria-label="新增註記" aria-haspopup="menu" aria-expanded={toolPanel === "add"} disabled={exporting} onClick={(event) => { panelTriggerRef.current = event.currentTarget; setToolPanel((previous) => previous === "add" ? null : "add"); }}><PlusIcon><FiPlus /></PlusIcon></ToolButton>}
          <ToolButton type="button" title={item.label} aria-label={item.label} data-handwriting-tool={item.id} aria-pressed={tool === item.id} $selected={tool === item.id} disabled={exporting} onClick={() => changeTool(item.id)}>{item.icon}</ToolButton>
        </React.Fragment>)}</PrimaryTools>
          <ToolButton type="button" title="復原上一動" aria-label="復原" disabled={exporting || !documentState.past.length} onClick={() => restoreHistory("undo")}><FiRotateCcw /></ToolButton>
          <ToolButton type="button" title={showOptions ? "收合手寫選項" : "展開手寫選項"} aria-label={showOptions ? "收合手寫選項" : "展開手寫選項"} aria-expanded={showOptions} onClick={() => { setToolPanel(null); setShowOptions((value) => !value); }}><FiChevronRight style={{ transform: showOptions ? "rotate(180deg)" : undefined }} /></ToolButton>
        </PrimaryRow>
        <PaletteRow><PaletteScroll>
          <BrushPreview type="button" title={isEraserTool ? "選擇筆色；雙點返回上一個工具" : "選擇筆色；雙點切換筆／橡皮擦"} aria-label="選擇筆色" onDoubleClick={toggleEraser} aria-expanded={toolPanel === "color"} disabled={exporting} onClick={toggleColorPanel}>{isEraserTool ? <OutlineIcon kind="eraser" /> : <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true"><path d="M7 19 19 7" stroke={color === "#252d3d" ? "#fff" : color} strokeWidth={Math.min(10, width + 1)} strokeLinecap="round" /></svg>}</BrushPreview>
        </PaletteScroll>
          <SizeButton type="button" title={`${sizeLabel} ${selectedSize}`} aria-label={`選擇${sizeLabel}`} aria-expanded={toolPanel === "width"} disabled={exporting || !isWidthControl} onClick={toggleWidthPanel}>
            {isTextTool ? <FiType /> : <span style={{ width: Math.min(24, 5 + selectedSize * 1.6), height: Math.min(24, 5 + selectedSize * 1.6) }} />}<small>{selectedSize}</small>
          </SizeButton>
        </PaletteRow>
      </Toolbar>
      {toolPanel === "add" && <ToolPopover ref={toolPopoverRef} style={popoverPosition || undefined} role="menu" aria-label="新增註記選單"><AddTextButton type="button" role="menuitem" disabled={exporting} onClick={() => { changeTool("text"); surfaceRef.current?.querySelector('[role="application"]')?.focus({ preventScroll: true }); }}><FiType />新增文字註記</AddTextButton><PanelHint>選擇後點擊頁面，即可輸入文字。</PanelHint></ToolPopover>}
      {toolPanel === "width" && isWidthControl && <ToolPopover ref={toolPopoverRef} style={popoverPosition || undefined} role="group" aria-label={`${sizeLabel}選項`}>
        <PanelTitle>{sizeLabel}</PanelTitle>
        <SizePresets>{sizePresets.map((value) => <PresetButton key={value} type="button" aria-label={`${sizeLabel} ${value}`} aria-pressed={selectedSize === value} $selected={selectedSize === value} $eraser={isEraserTool} disabled={exporting} onClick={() => isTextTool ? changeTextSize(value) : setWidth(value)}>{isTextTool ? <span style={{ fontSize: Math.min(23, value / 2 + 9) }}>A</span> : <PresetDot style={{ width: isEraserTool ? 6 + value / 10 : Math.min(28, 3 + value), height: isEraserTool ? 6 + value / 10 : Math.min(28, 3 + value) }} />}<small>{value}</small></PresetButton>)}</SizePresets>
        <WidthLabel>自訂<input type="range" aria-label={sizeLabel} min={isTextTool ? 12 : isEraserTool ? 1 : 20} max={isTextTool ? 48 : isEraserTool ? 72 : 32} value={selectedSize} disabled={exporting} onChange={(event) => isTextTool ? changeTextSize(Number(event.target.value)) : setWidth(Number(event.target.value))} /><span>{selectedSize}</span></WidthLabel>
        {isEraserTool && <PanelHint>範圍內的整個筆畫或文字會被擦除；放開後返回上一個工具。</PanelHint>}
      </ToolPopover>}
      {toolPanel === "color" && <ToolPopover ref={toolPopoverRef} style={popoverPosition || undefined} role="group" aria-label="筆色選項"><PanelTitle>筆色</PanelTitle><ExpandedColors>
        {colors.map((value) => <ColorButton key={value} type="button" aria-label={`筆色 ${value}`} aria-pressed={color === value} title={`筆色 ${value}`} $color={value} $selected={color === value} disabled={exporting} onClick={() => changeColor(value)} />)}
        <ColorInput type="color" aria-label="自訂筆色" title="自訂筆色" value={color} disabled={exporting} onChange={(event) => changeColor(event.target.value)} />
      </ExpandedColors></ToolPopover>}
      <AuxiliaryRow hidden={!showOptions}><PanelTitle>手寫選項</PanelTitle>
        <ToolGroup><AuxButton type="button" title="重做" aria-label="重做" disabled={exporting || !documentState.future.length} onClick={() => restoreHistory("redo")}><FiRotateCw /></AuxButton>
          <AuxButton type="button" title="刪除選取的手寫" aria-label="刪除選取的手寫" disabled={exporting || !selection.length} onClick={deleteSelection}><FiTrash2 /></AuxButton>
        </ToolGroup>
        <ToolGroup><AuxButton type="button" title="縮小" aria-label="縮小手寫預覽" disabled={exporting || zoom <= 0.5} onClick={() => setZoom((value) => Math.max(0.5, Math.round((value - 0.25) * 100) / 100))}><FiMinus /></AuxButton>
          <ZoomButton type="button" title="重設縮放" aria-label="重設手寫縮放" disabled={exporting} onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</ZoomButton>
          <AuxButton type="button" title="放大" aria-label="放大手寫預覽" disabled={exporting || zoom >= 2} onClick={() => setZoom((value) => Math.min(2, Math.round((value + 0.25) * 100) / 100))}><FiPlus /></AuxButton>
          <AuxButton type="button" title={fingerDrawing ? "手指書寫已開啟" : "手指接觸不移動頁面；點擊開啟手指書寫"} aria-label="手指書寫" aria-pressed={fingerDrawing} $selected={fingerDrawing} disabled={exporting} onClick={() => setFingerDrawing((value) => !value)}><FiMousePointer /></AuxButton></ToolGroup>
        <WidthLabel title="畫完保持筆尖接觸約 0.7 秒，自動修正直線、圓形、橢圓或拋物線"><input type="checkbox" checked={smartShapes} onChange={(event) => { clearTimeout(shapeTimerRef.current); setSmartShapes(event.target.checked); }} />停筆修形</WidthLabel>
        {allowPdfExport && <ToolGroup>{exportButton}</ToolGroup>}
      </AuxiliaryRow>
      {persistent && <SaveRow title={documentState.error || (unsaved ? "手寫尚未儲存；關閉紀錄會捨棄變更" : documentState.entries.length ? "手寫已儲存在此瀏覽器" : "按「儲存手寫」才會保存")}><Status role={documentState.error ? "alert" : "status"} $error={Boolean(documentState.error)}>{documentState.error || (unsaved ? "手寫尚未儲存；關閉紀錄會捨棄變更" : documentState.entries.length ? "手寫已儲存在此瀏覽器" : "按「儲存手寫」才會保存")}<span>{tool === "lasso" ? "圈選後可拖曳移動或刪除" : tool === "text" ? "點擊頁面加入文字" : tool === "eraser" ? "擦除整個筆畫或文字" : tool === "hand" ? "拖曳頁面瀏覽" : tool === "pen" ? "Apple Pencil 輕壓細・重壓粗" : fingerDrawing ? "手指與觸控筆皆可書寫" : "頁面已固定・觸控筆書寫"}</span></Status>
        <SaveButton type="button" aria-label="儲存手寫" title="將這份手寫儲存在此瀏覽器" disabled={exporting || documentState.readError || !unsaved} onClick={saveHandwriting}><FiSave /><span>儲存手寫</span></SaveButton></SaveRow>}
      {actionNotice && actionNotice !== "已儲存這份手寫" && <Status role="status">{actionNotice}</Status>}
      {exportFeedback && <Status role={exportState.status === "error" ? "alert" : "status"} $error={exportState.status === "error"}>{exportFeedback}</Status>}
    </ToolbarWrap>}
    {!active && allowPdfExport && <ExportActions>{persistent && unsaved && <Status role="status">手寫尚未儲存；關閉紀錄會捨棄變更</Status>}{exportFeedback && <Status role={exportState.status === "error" ? "alert" : "status"} $error={exportState.status === "error"}>{exportFeedback}</Status>}{persistent && unsaved && <SaveButton type="button" aria-label="儲存手寫" title="將這份手寫儲存在此瀏覽器" disabled={exporting || documentState.readError} onClick={saveHandwriting}><FiSave /><span>儲存手寫</span></SaveButton>}{exportButton}</ExportActions>}
    {!active && documentState.error && <Status role="alert" $error>{documentState.error}</Status>}
    {active && inkMenu && <InkMenu role="menu" aria-label={inkMenu.kind === "paste" ? "貼上手寫選單" : "選取手寫選單"} style={{ left: inkMenu.left, top: inkMenu.top }} data-ink-menu>
      {inkMenu.kind === "selection" ? <>
        <InkMenuButton type="button" role="menuitem" disabled={exporting || !selection.length} onClick={() => copySelection()}>複製</InkMenuButton>
        <InkMenuButton type="button" role="menuitem" disabled={exporting || !selection.length} onClick={() => copySelection(true)}>剪下</InkMenuButton>
        <InkMenuButton type="button" role="menuitem" disabled={exporting || !selection.length} onClick={duplicateSelection}>建立副本</InkMenuButton>
        <InkMenuButton type="button" role="menuitem" disabled={exporting} onClick={captureSelection}>區塊截圖</InkMenuButton>
        <InkMenuButton type="button" role="menuitem" $danger disabled={exporting || !selection.length} onClick={deleteSelection}>刪除</InkMenuButton>
      </> : <InkMenuButton type="button" role="menuitem" disabled={exporting || !inkClipboardRef.current} onClick={pasteInk}>貼上</InkMenuButton>}
    </InkMenu>}
    <Viewport ref={viewportRef} $hasSidebar={active || allowPdfExport} $active={active} $locked={active && tool !== "hand"}>
      <ZoomPlane style={{ width: dimensions.width * effectiveZoom, height: dimensions.height > 1 ? dimensions.height * effectiveZoom : "auto" }}>
        <Surface ref={surfaceRef} style={{ width: dimensions.width, transform: `scale(${effectiveZoom})` }}>
          <DocumentLayer ref={previewRef} $paper={active || entries.length > 0}>{children}</DocumentLayer>
          <InkLayer role={active ? "application" : undefined} aria-label={active ? "手寫畫布" : "手寫註記"} aria-hidden={!active && !entries.length} tabIndex={active ? 0 : undefined}
            width="100%" height="100%" viewBox={`0 0 ${DOCUMENT_UNITS} ${Math.max(docHeight, 1)}`} preserveAspectRatio="none" $active={active && !exporting} $tool={tool}
            onContextMenu={(event) => { if (active && inkClipboardRef.current && !exporting) { event.preventDefault(); resetGesture(); setSelection([]); setSelectionRegion(null); openInkMenu("paste", pointFromEvent(event)); } }}
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
            onPointerCancel={(event) => {
              cancelLongPress();
              const gesture = gestureRef.current;
              if (gesture?.pointerId !== event.pointerId) return;
              finishGesture();
            }}
            onLostPointerCapture={(event) => { if (gestureRef.current?.pointerId === event.pointerId) finishGesture(); }}>
            {entries.map((entry) => entry.type === "stroke" ? <InkStroke key={entry.id} stroke={entry} selected={selection.includes(entry.id)} />
              : textEditor?.id === entry.id ? null : <text key={entry.id} data-text-annotation={entry.id} x={entry.x} y={entry.y} fill={entry.color} fontSize={entry.fontSize} fontFamily="sans-serif" style={{ whiteSpace: "pre" }}>{entry.text.split("\n").map((line, index) => <tspan key={index} x={entry.x} dy={index === 0 ? "1em" : "1.3em"}>{line || " "}</tspan>)}</text>)}
            {draft && <InkStroke stroke={draft} />}
            {eraserPreview && <EraserPreview points={eraserPreview.points} radius={eraserPreview.radius} />}
            {lasso.length > 0 && <polyline points={lasso.map((point) => `${point.x},${point.y}`).join(" ")} fill="rgba(81,151,255,.1)" stroke="#5197ff" strokeWidth="1.8" strokeDasharray="5 4" />}
            {box && <rect x={box.x} y={box.y} width={box.width} height={box.height} rx="3" fill="rgba(81,151,255,.08)" stroke="#5197ff" strokeWidth="1.5" strokeDasharray="5 3" />}
          </InkLayer>
          {active && textEditor && <TextEditor style={{ left: `${textEditor.x / DOCUMENT_UNITS * 100}%`, top: textEditor.y / DOCUMENT_UNITS * dimensions.width, fontSize: textEditor.fontSize / DOCUMENT_UNITS * dimensions.width, color: textEditor.color }}>
            <textarea ref={textRef} aria-label="手寫文字" rows={3} value={textEditor.text} onChange={(event) => setTextEditor((editor) => ({ ...editor, text: event.target.value }))} onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Escape") { setTextEditor(null); textEditorRef.current = null; }
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) { event.preventDefault(); saveText(); }
            }} />
            <div><button type="button" aria-label="完成手寫文字" title="完成文字註記；按儲存手寫才會保存" onClick={saveText}><FiCheck /></button><button type="button" aria-label="取消手寫文字" title="取消" onClick={() => { setTextEditor(null); textEditorRef.current = null; }}><FiX /></button></div>
          </TextEditor>}
        </Surface>
      </ZoomPlane>
    </Viewport>
  </Wrapper>;
}

const InkStroke = memo(function InkStroke({ stroke, selected }) {
  return <path data-ink-stroke={stroke.id} d={strokeOutline(stroke)} fill={stroke.color} opacity={stroke.tool === "highlighter" ? 0.33 : stroke.tool === "pencil" ? 0.72 : 1}
    fillRule="nonzero" style={{ fillRule: "nonzero", mixBlendMode: stroke.tool === "highlighter" ? "multiply" : undefined }} stroke={selected ? "#5197ff" : undefined} strokeWidth={selected ? 0.8 : undefined} />;
});

function EraserPreview({ points, radius }) {
  const point = points[points.length - 1];
  return <g data-eraser-preview aria-hidden="true" pointerEvents="none">
    {points.length === 1 ? <circle cx={point.x} cy={point.y} r={radius} fill="#60a5fa" fillOpacity="0.25" />
      : <polyline data-eraser-trail points={points.map((sample) => `${sample.x},${sample.y}`).join(" ")} fill="none" stroke="#60a5fa" strokeOpacity="0.25" strokeWidth={radius * 2} strokeLinecap="round" strokeLinejoin="round" />}
    <circle cx={point.x} cy={point.y} r={radius} fill="none" stroke="#2463eb" strokeWidth="1.5" strokeDasharray="5 4" vectorEffect="non-scaling-stroke" />
  </g>;
}

const Wrapper = styled.div`width: 100%; min-width: 0; position: relative; display: grid; grid-template-columns: ${({ $hasSidebar }) => $hasSidebar ? "48px minmax(0, 1fr)" : "minmax(0, 1fr)"}; align-items: start; gap: 10px;`;
const ToolbarWrap = styled.div`position: sticky; top: 0; grid-column: 1; grid-row: 1; z-index: 12; width: 48px; padding: 2px 0 6px;`;
const Toolbar = styled.div`display: flex; flex-direction: column; width: 100%; max-height: calc(100dvh - 264px); overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin; box-sizing: border-box; color: #e1e5ed; background: #101114; border: 1px solid #555a62; border-radius: 12px; box-shadow: 0 3px 12px rgba(13, 19, 31, .15);`;
const PrimaryRow = styled.div`display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 6px;`;
const PrimaryTools = styled.div`display: flex; flex-direction: column; align-items: center; gap: 4px;`;
const ToolButton = styled.button`position: relative; width: 30px; height: 30px; padding: 4px; flex: 0 0 auto; border: 1px solid transparent; border-radius: 10px; color: ${({ $selected }) => $selected ? "#fff" : "#cdd1d9"}; background: transparent; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; svg { width: 21px; height: 21px; } ${({ $selected }) => $selected && `&::after { content: ""; position: absolute; left: 10px; right: 10px; bottom: 0; height: 2px; border-radius: 2px; background: #8dc1ff; }`} &:hover:not(:disabled) { background: #29313e; } &:focus-visible { outline: 2px solid #a9ccff; outline-offset: 2px; } &:disabled { opacity: .28; cursor: default; } `;
const EraserButton = styled(ToolButton)`touch-action: none; -webkit-user-select: none; user-select: none; -webkit-touch-callout: none; &, & * { -webkit-user-select: none; user-select: none; } width: 48px; height: 52px; margin-bottom: 8px; flex-direction: column; gap: 3px; border: 1px solid ${({ $selected }) => $selected ? "#91baff" : "var(--app-border, #d7dee4)"}; border-radius: 13px; background: ${({ $selected }) => $selected ? "#23354d" : "var(--app-surface, #fff)"}; color: ${({ $selected }) => $selected ? "#b7d7ff" : "var(--app-text, #27313c)"}; box-shadow: 0 3px 10px #0001; svg { width: 22px; height: 22px; } small { font-size: 9px; line-height: 1; } &::after { display: none; } &:hover:not(:disabled) { background: ${({ $selected }) => $selected ? "#23354d" : "var(--app-surface, #fff)"}; } @media (hover: hover) and (pointer: fine) { &:hover:not(:disabled) { box-shadow: 0 3px 12px #0003; } }`;
const PlusIcon = styled.span`display: flex; align-items: center; justify-content: center; width: 23px; height: 23px; border: 1.4px solid currentColor; border-radius: 50%; svg { width: 17px; height: 17px; }`;
const PaletteRow = styled.div`display: flex; flex-direction: column; align-items: center; gap: 9px; padding: 8px 6px; border-top: 1px solid #555a62;`;
const PaletteScroll = styled.div`display: flex; flex-direction: column; align-items: center; padding: 3px 0;`;
const ExpandedColors = styled.div`display: grid; grid-template-columns: repeat(5, 28px); justify-content: space-between; gap: 12px 8px; padding: 4px 2px 9px;`;
const BrushPreview = styled.button`display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; width: 32px; height: 32px; padding: 3px; border: 1px solid #a3b8ce; outline: 1.5px solid #5eabf2; outline-offset: 2px; border-radius: 50%; background: #111318; color: #e1e5ed; cursor: pointer; &:focus-visible { outline: 2px solid #fff; } &:disabled { opacity: .3; cursor: default; } @media (max-width: 640px) { width: 30px; height: 30px; }`;
const ColorButton = styled.button`display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; width: 28px; height: 28px; border-radius: 50%; padding: 0; border: 1px solid #ffffff22; outline: ${({ $selected }) => $selected ? "2px solid #8dc1ff" : "none"}; outline-offset: 3px; background: ${({ $color }) => $color}; cursor: pointer; &:focus-visible { outline: 2px solid #fff; outline-offset: 3px; } &:disabled { opacity: .3; cursor: default; }`;
const ColorInput = styled.input`width: 28px; height: 28px; flex: 0 0 auto; padding: 0; border: 0; border-radius: 50%; background: transparent; cursor: pointer; &::-webkit-color-swatch-wrapper { padding: 0; } &::-webkit-color-swatch { border: 1px solid #ffffff66; border-radius: 50%; } &::-moz-color-swatch { border: 1px solid #ffffff66; border-radius: 50%; }`;
const SizeButton = styled(BrushPreview)`position: relative; flex: 0 0 auto; margin: 1px 0; color: #e1e5ed;  > span { display: block; border-radius: 50%; background: #d5d8dd; } > small { position: absolute; right: -4px; bottom: -4px; min-width: 14px; padding: 1px 2px; border-radius: 5px; font-size: 9px; line-height: 1.2; background: #29313e; color: #e1e5ed; } svg { width: 24px; height: 24px; }`;
const ToolPopover = styled.div`position: absolute; top: 0; left: calc(100% + 8px); z-index: 25; width: min(220px, calc(100vw - 90px)); max-height: calc(100dvh - 16px); overflow-y: auto; overscroll-behavior: contain; box-sizing: border-box; padding: 12px; border: 1px solid #555a62; border-radius: 10px; color: #e1e5ed; background: #15181d; box-shadow: 0 6px 24px #0004;`;
const PanelTitle = styled.div`font-size: 12px; margin-bottom: 12px; color: #cdd1d9;`;
const SizePresets = styled.div`display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px; margin-bottom: 9px;`;
const PresetButton = styled.button`display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 6px; width: 100%; height: ${({ $eraser }) => $eraser ? "56px" : "40px"}; padding: 4px; border: 1px solid ${({ $selected }) => $selected ? "#8dc1ff" : "#40444c"}; border-radius: 8px; color: #e1e5ed; background: ${({ $selected }) => $selected ? "#23354d" : "transparent"}; cursor: pointer; small { font-size: 10px; } &:hover:not(:disabled) { background: #29313e; } &:focus-visible { outline: 2px solid #a9ccff; outline-offset: 2px; } &:disabled { opacity: .3; }`;
const PresetDot = styled.span`display: block; border-radius: 50%; margin-top: auto; margin-bottom: auto; background: #e1e5ed;`;
const WidthLabel = styled.label`display: flex; gap: 8px; align-items: center; font-size: 11px; color: #c5ccd8; input { width: 100%; min-width: 0; flex: 1; accent-color: #91baff; cursor: pointer; } span { min-width: 18px; font-variant-numeric: tabular-nums; }`;
const AddTextButton = styled.button`display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 6px; border: 0; border-radius: 8px; color: inherit; background: transparent; cursor: pointer; font: inherit; font-size: 13px; &:hover { background: #29313e; } &:focus-visible { outline: 2px solid #a9ccff; } svg { width: 20px; height: 20px; }`;
const PanelHint = styled.div`font-size: 11px; color: #aab2be; margin: 7px 6px 2px;`;
const AuxiliaryRow = styled.div`position: absolute; top: 0; left: calc(100% + 8px); z-index: 24; display: flex; flex-direction: column; align-items: stretch; gap: 9px; width: min(220px, calc(100vw - 90px)); max-height: calc(100dvh - 130px); overflow-y: auto; overscroll-behavior: contain; box-sizing: border-box; padding: 12px; border: 1px solid var(--app-border, #555a62); border-radius: 12px; background: var(--app-surface, #fff); box-shadow: 0 6px 24px #0004; &[hidden] { display: none; }`;
const ToolGroup = styled.div`display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 3px; padding-bottom: 5px; border-bottom: 1px solid var(--app-border, #d7dee4); &:last-child { border: 0; padding-bottom: 0; }`;
const AuxButton = styled(ToolButton)`width: 30px; height: 30px; padding: 5px; color: var(--app-text, #27313c); svg { width: 18px; height: 18px; } &:hover:not(:disabled) { background: var(--app-surface-soft, #f0f0f0); } ${({ $selected }) => $selected && `background: #e8f0ff; color: #2463eb;`}`;
const HandButton = styled(EraserButton)`width: 48px; height: 44px; margin-bottom: 8px; padding: 5px 3px; flex-direction: column; gap: 3px; border-radius: 13px; svg { width: 19px; height: 19px; } span { font-size: 9px; line-height: 1; }`;
const ZoomButton = styled(AuxButton)`width: 48px; padding: 4px; font-size: 12px; font-variant-numeric: tabular-nums;`;
const PdfExportButton = styled.button`display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 35px; padding: 6px 10px; border: 1px solid ${({ $toolbar }) => $toolbar ? "#40444c" : "var(--app-border, #d7dee4)"}; border-radius: 10px; background: ${({ $toolbar }) => $toolbar ? "transparent" : "var(--app-surface, #fff)"}; color: ${({ $toolbar }) => $toolbar ? "#e1e5ed" : "var(--app-text, #27313c)"}; font: inherit; font-size: 12px; cursor: pointer; svg { width: 18px; height: 18px; } &:hover:not(:disabled) { background: ${({ $toolbar }) => $toolbar ? "#29313e" : "var(--app-surface-soft, #f0f0f0)"}; } &:focus-visible { outline: 2px solid #91baff; outline-offset: 2px; } &:disabled { opacity: .5; cursor: wait; }`;
const SaveRow = styled.div`position: relative; width: 100%; margin-top: 7px; > button { width: 100%; padding: 4px; span { display: none; } } > [role="status"] { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; } &:hover > [role="status"], &:focus-within > [role="status"] { left: calc(100% + 8px); top: 0; width: min(220px, calc(100vw - 90px)); height: auto; margin: 0; padding: 9px 12px; clip: auto; white-space: normal; background: var(--app-surface, #fff); border: 1px solid var(--app-border, #d7dee4); border-radius: 8px; box-shadow: 0 4px 16px #0003; }`;
const SaveButton = styled(PdfExportButton)`flex: 0 0 auto; height: 30px; gap: 5px; padding: 5px 9px; color: #2463eb; border-color: #b8d2ff; background: #f3f7ff; white-space: nowrap; svg { width: 16px; height: 16px; } &:disabled { cursor: default; }`;
const ExportActions = styled.div`position: sticky; top: 0; grid-column: 1; grid-row: 1; display: flex; flex-direction: column; gap: 8px; width: 48px; padding: 2px 0; > button { width: 48px; padding: 4px; font-size: 10px; gap: 3px; span { display: none; } } > div { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }`;
const Status = styled.div`max-width: 600px; margin: 0 auto; box-sizing: border-box; display: flex; flex-wrap: wrap; justify-content: space-between; gap: 4px 16px; padding: 5px 8px 0; font-size: 11px; color: ${({ $error }) => $error ? "var(--app-danger, #b42318)" : "var(--app-muted-text, #667085)"}; span { color: inherit; }`;
const Viewport = styled.div`
  width: 100%; min-width: 0; grid-column: ${({ $hasSidebar }) => $hasSidebar ? 2 : 1}; grid-row: 1;
  overflow-x: ${({ $locked }) => $locked ? "hidden" : "auto"}; overflow-y: hidden;
  padding-bottom: 2px; touch-action: ${({ $active }) => $active ? "none" : "auto"};
  overscroll-behavior-x: contain;
  overscroll-behavior-y: ${({ $locked }) => $locked ? "contain" : "auto"};
`;
const ZoomPlane = styled.div`position: relative; margin: 0 auto;`;
const Surface = styled.div`position: relative; transform-origin: top left;`;
const DocumentLayer = styled.div`display: flow-root; width: 100%; color: var(--app-text, #27313c); background: var(--app-surface, #fff);`;
const InkLayer = styled.svg`position: absolute; inset: 0; z-index: 2; overflow: visible; pointer-events: ${({ $active }) => $active ? "auto" : "none"}; touch-action: ${({ $active }) => $active ? "none" : "auto"}; user-select: none; cursor: ${({ $tool }) => $tool === "hand" ? "grab" : $tool === "text" ? "text" : "crosshair"}; outline: none;`;
const InkMenu = styled.div`position: fixed; z-index: 50; width: 200px; padding: 5px; border: 1px solid var(--app-border, #d7dee4); border-radius: 12px; background: var(--app-surface, #fff); color: var(--app-text, #27313c); box-shadow: 0 8px 28px #0003;`;
const InkMenuButton = styled.button`display: block; width: 100%; padding: 10px 12px; border: 0; border-radius: 7px; text-align: left; background: transparent; color: ${({ $danger }) => $danger ? "#e44b53" : "inherit"}; font: inherit; font-size: 13px; cursor: pointer; &:hover:not(:disabled) { background: var(--app-surface-soft, #edf1f5); } &:focus-visible { outline: 2px solid #91baff; } &:disabled { opacity: .4; }`;
const TextEditor = styled.div`position: absolute; z-index: 4; width: min(320px, 75%); min-width: 150px; padding: 5px; border: 1px solid #5197ff; border-radius: 8px; background: #fff; box-shadow: 0 4px 18px #16335b22; textarea { width: 100%; resize: vertical; border: 0; outline: none; font: inherit; color: inherit; line-height: 1.3; background: transparent; } div { display: flex; justify-content: flex-end; gap: 4px; } button { display: inline-flex; justify-content: center; align-items: center; width: 30px; height: 28px; padding: 3px; border: 0; border-radius: 5px; color: #2463eb; background: #eef4ff; cursor: pointer; }`;
